import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './TeamNotes.css';

const API_BASE = process.env.REACT_APP_API_BASE;

const TEAM_ROLE_ID = process.env.REACT_APP_TEAM_ROLE_ID;

const TeamNotes = () => {
    const [teamMembers, setTeamMembers] = useState([]);
    const [notes, setNotes] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedUser, setSelectedUser] = useState(null);
    const [newNote, setNewNote] = useState('');
    const [editingNote, setEditingNote] = useState(null);
    const [editContent, setEditContent] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);
            const token = localStorage.getItem('discord_token');
            const headers = { Authorization: `Bearer ${token}` };

            // Fetch team members, notes, and guild members (for Discord roles) in parallel
            const [membersRes, notesRes] = await Promise.all([
                axios.get(`${API_BASE}/api/users`, { headers }),
                axios.get(`${API_BASE}/api/user-notes`, { headers })
            ]);

            // Fetch Discord roles for team filtering
            let rolesMap = {};
            try {
                const guildMembersRes = await axios.get(`${API_BASE}/api/guild-members`, { headers });
                if (guildMembersRes.data && guildMembersRes.data.members) {
                    guildMembersRes.data.members.forEach(member => {
                        rolesMap[member.id] = member.roles || [];
                    });
                }
            } catch (guildErr) {
                console.warn('Could not fetch guild members, using fallback:', guildErr);
            }

            // Filter only users with the Team role
            const allUsers = Array.isArray(membersRes.data) ? membersRes.data : [];
            const teamOnly = allUsers.filter(user => {
                const userRoles = rolesMap[user.user_id] || [];
                return userRoles.includes(TEAM_ROLE_ID);
            });

            setTeamMembers(teamOnly);
            setNotes(Array.isArray(notesRes.data) ? notesRes.data : []);
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Fehler beim Laden der Daten');
            setTeamMembers([]);
            setNotes([]);
        } finally {
            setLoading(false);
        }
    };

    const handleAddNote = async (userId) => {
        if (!newNote.trim()) return;

        try {
            const token = localStorage.getItem('discord_token');
            await axios.post(`${API_BASE}/api/user-notes`, {
                user_id: userId,
                note: newNote.trim()
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setNewNote('');
            setSelectedUser(null);
            fetchData(); // Refresh data
        } catch (err) {
            console.error('Error adding note:', err);
            alert('Fehler beim Hinzufügen der Notiz');
        }
    };

    const handleEditNote = async (noteId) => {
        if (!editContent.trim()) return;

        try {
            const token = localStorage.getItem('discord_token');
            await axios.put(`${API_BASE}/api/user-notes/${noteId}`, {
                note: editContent.trim()
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setEditingNote(null);
            setEditContent('');
            fetchData(); // Refresh data
        } catch (err) {
            console.error('Error editing note:', err);
            alert('Fehler beim Bearbeiten der Notiz');
        }
    };

    const handleDeleteNote = async (noteId) => {
        if (!window.confirm('Notiz wirklich löschen?')) return;

        try {
            const token = localStorage.getItem('discord_token');
            await axios.delete(`${API_BASE}/api/user-notes/${noteId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            fetchData(); // Refresh data
        } catch (err) {
            console.error('Error deleting note:', err);
            alert('Fehler beim Löschen der Notiz');
        }
    };

    const getUserNotes = (userId) => {
        return notes.filter(note => note.user_id === userId);
    };

    // Filter team members based on search term
    const filteredTeamMembers = teamMembers.filter(member => {
        if (!searchTerm.trim()) return true;

        const searchLower = searchTerm.toLowerCase();
        const displayName = member.server_nickname || member.global_name || member.username || '';
        const globalName = member.global_name || member.username || '';
        const roles = member.roles || '';

        return displayName.toLowerCase().includes(searchLower) ||
            globalName.toLowerCase().includes(searchLower) ||
            roles.toLowerCase().includes(searchLower);
    });

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="team-notes-container">
                <div className="dashboard-main">
                    <div className="team-notes-hero">
                        <h2>📝 Team Notizen</h2>
                        <p className="muted">Verwalte Notizen zu Teammitgliedern</p>
                    </div>
                    <div className="team-loading">
                        <div className="spinner"></div>
                        <h2>Team Notizen werden geladen...</h2>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="team-notes-container">
                <div className="dashboard-main">
                    <div className="team-notes-hero">
                        <h2>📝 Team Notizen</h2>
                        <p className="muted">Verwalte Notizen zu Teammitgliedern</p>
                    </div>
                    <div className="team-notes-info">
                        <div className="team-warning-banner">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            <strong>Fehler:</strong> {error}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="team-notes-container">
            <div className="dashboard-main">
                <div className="team-notes-hero">
                    <h2>📝 Team Notizen</h2>
                    <p className="muted">Verwalte Notizen zu Teammitgliedern</p>
                    <p className="team-count">{teamMembers.length} Teammitglieder</p>
                </div>

                <div className="team-notes-info">
                    <div className="team-info-banner">
                        <i className="fas fa-info-circle me-2"></i>
                        Hier können Notizen zu Teammitgliedern hinzugefügt und verwaltet werden.
                    </div>

                    {/* Search Field */}
                    <div className="team-search-section">
                        <div className="team-search-container">
                            <i className="fas fa-search team-search-icon"></i>
                            <input
                                type="text"
                                className="team-search-input"
                                placeholder="Teammitglied suchen..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            {searchTerm && (
                                <button
                                    className="team-clear-search"
                                    onClick={() => setSearchTerm('')}
                                    title="Suche löschen"
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                        {searchTerm && (
                            <div className="team-search-results">
                                {filteredTeamMembers.length} von {teamMembers.length} Teammitgliedern gefunden
                            </div>
                        )}
                    </div>

                    <div className="team-members-section">
                        <div className="team-members-container">
                            {teamMembers.length === 0 ? (
                                <p className="team-empty">Keine Teammitglieder gefunden.</p>
                            ) : (
                                filteredTeamMembers.map(member => {
                                    const userNotes = getUserNotes(member.user_id);
                                    const initials = (member.server_nickname || member.global_name || member.username || '?').charAt(0).toUpperCase();
                                    
                                    return (
                                        <div key={member.user_id} className="team-member-card">
                                            <div className="team-member-header">
                                                <div className="team-member-info">
                                                    {member.avatar_url ? (
                                                        <img
                                                            src={member.avatar_url}
                                                            alt="Avatar"
                                                            className="team-member-avatar"
                                                            style={{ objectFit: 'cover' }}
                                                        />
                                                    ) : (
                                                        <div className="team-member-avatar">
                                                            {initials}
                                                        </div>
                                                    )}
                                                    <div className="team-member-details">
                                                        <h4 className="team-member-name">{member.server_nickname || member.global_name || member.username}</h4>
                                                        <p className="team-member-roles">
                                                            {member.server_nickname && (member.global_name || member.username) !== member.server_nickname
                                                                ? `${member.global_name || member.username} • `
                                                                : ''}
                                                            {member.roles || 'Keine Rollen'}
                                                        </p>
                                                    </div>
                                                </div>
                                                {selectedUser !== member.user_id && (
                                                    <button
                                                        className="team-btn-add-note"
                                                        onClick={() => setSelectedUser(member.user_id)}
                                                        title="Notiz hinzufügen"
                                                    >
                                                        ➕
                                                    </button>
                                                )}
                                            </div>

                                            {/* Existing Notes */}
                                            {userNotes.length > 0 && (
                                                <div className="team-notes-compact">
                                                    {userNotes.map(note => (
                                                        <div key={note.id} className="team-note-compact">
                                                            {editingNote === note.id ? (
                                                                <div className="team-note-edit">
                                                                    <textarea
                                                                        className="team-note-input"
                                                                        value={editContent}
                                                                        onChange={(e) => setEditContent(e.target.value)}
                                                                        rows="2"
                                                                    />
                                                                    <div className="team-note-form-actions">
                                                                        <button
                                                                            className="team-btn-small confirm"
                                                                            onClick={() => handleEditNote(note.id)}
                                                                        >
                                                                            ✓
                                                                        </button>
                                                                        <button
                                                                            className="team-btn-small cancel"
                                                                            onClick={() => {
                                                                                setEditingNote(null);
                                                                                setEditContent('');
                                                                            }}
                                                                        >
                                                                            ✕
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="team-note-content">
                                                                    <p className="team-note-text">{note.note}</p>
                                                                    <div className="team-note-footer">
                                                                        <small className="team-note-meta">
                                                                            {note.creator_global_name || note.creator_username} • {formatDate(note.created_at)}
                                                                        </small>
                                                                        <div className="team-note-actions">
                                                                            <button
                                                                                className="team-btn-icon"
                                                                                onClick={() => {
                                                                                    setEditingNote(note.id);
                                                                                    setEditContent(note.note);
                                                                                }}
                                                                                title="Bearbeiten"
                                                                            >
                                                                                ✏️
                                                                            </button>
                                                                            <button
                                                                                className="team-btn-icon danger"
                                                                                onClick={() => handleDeleteNote(note.id)}
                                                                                title="Löschen"
                                                                            >
                                                                                🗑️
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Add New Note */}
                                            {selectedUser === member.user_id && (
                                                <div className="team-add-note">
                                                    <textarea
                                                        className="team-note-input"
                                                        placeholder="Notiz eingeben..."
                                                        value={newNote}
                                                        onChange={(e) => setNewNote(e.target.value)}
                                                        rows="2"
                                                    />
                                                    <div className="team-note-form-actions">
                                                        <button
                                                            className="team-btn-small confirm"
                                                            onClick={() => handleAddNote(member.user_id)}
                                                            disabled={!newNote.trim()}
                                                        >
                                                            ✓
                                                        </button>
                                                        <button
                                                            className="team-btn-small cancel"
                                                            onClick={() => {
                                                                setSelectedUser(null);
                                                                setNewNote('');
                                                            }}
                                                        >
                                                            ✕
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TeamNotes;
